import { io, type Socket } from 'socket.io-client';
import { ApiError } from './api';
import { rememberActiveSession, SESSION_PROTOCOL_VERSION } from './session-core';
import { trackAnalytics } from './analytics';

const challengeKey = 'utage.activeChallengeToken';
export type ChallengeStatus = 'active' | 'claimed' | 'cancelled' | 'expired';
export type MomentKey = 'deep' | 'same_brain' | 'read_me' | 'dont_match';
export interface ChallengeSnapshot {
  protocolVersion: 1;
  challenge: {
    id: string; status: ChallengeStatus; momentKey: MomentKey; interactionType: 'same_brain' | 'read_me' | 'dont_match'; revision: number; customPromptId: string | null;
    creator: { firstName: string }; recipient: { firstName: string } | null; target: { firstName: string } | null;
    sessionId: string | null; expiresAt: string;
  };
  viewerRole: 'creator' | 'recipient' | 'unclaimed_recipient' | 'other';
}
export interface ChallengeShare { deepLink: string; fallbackShareUrl: string; preparedMessageId: string | null }
export interface CreatedChallenge { token: string; snapshot: ChallengeSnapshot; share: ChallengeShare | null }

export const rememberedChallengeToken = (): string | null => localStorage.getItem(challengeKey);
export const rememberChallenge = (token: string | null): void => {
  if (token) localStorage.setItem(challengeKey, token); else localStorage.removeItem(challengeKey);
};

const request = async <T>(path: string, initData: string, options?: RequestInit): Promise<T> => {
  const response = await fetch(path, {
    ...options, cache: 'no-store',
    headers: { Accept: 'application/json', Authorization: `tma ${initData}`, 'Content-Type': 'application/json', 'X-UTAGE-Protocol': String(SESSION_PROTOCOL_VERSION), ...options?.headers }
  });
  const body = await response.json().catch(() => ({})) as T & { error?: { code?: string; message?: string } };
  if (!response.ok) throw new ApiError(body.error?.message ?? 'Could not update this challenge.', body.error?.code);
  return body;
};

export const createFriendChallenge = (initData: string, interactionType: 'same_brain' | 'read_me' | 'dont_match'): Promise<CreatedChallenge> =>
  request('/challenges', initData, { method: 'POST', body: JSON.stringify({ actionId: crypto.randomUUID(), interactionType }) });
export const createFriendMomentChallenge = (initData: string, momentKey: MomentKey): Promise<CreatedChallenge> =>
  request('/challenges', initData, { method: 'POST', body: JSON.stringify({ actionId: crypto.randomUUID(), momentKey }) });
export const loadChallenge = (token: string, initData: string): Promise<ChallengeSnapshot> =>
  request(`/challenges/${encodeURIComponent(token)}`, initData);
export const claimChallenge = async (token: string, initData: string): Promise<ChallengeSnapshot> => {
  const result = await request<{ snapshot: ChallengeSnapshot }>(`/challenges/${encodeURIComponent(token)}/claim`, initData, { method: 'POST', body: JSON.stringify({ actionId: crypto.randomUUID() }) });
  if (result.snapshot.challenge.sessionId) rememberActiveSession(result.snapshot.challenge.sessionId);
  return result.snapshot;
};
export const cancelChallenge = async (token: string, initData: string): Promise<ChallengeSnapshot> => {
  const result = await request<{ snapshot: ChallengeSnapshot }>(`/challenges/${encodeURIComponent(token)}/cancel`, initData, { method: 'POST', body: JSON.stringify({ actionId: crypto.randomUUID() }) });
  return result.snapshot;
};
export const refreshShare = (token: string, initData: string): Promise<ChallengeShare> =>
  request(`/challenges/${encodeURIComponent(token)}/share`, initData, { method: 'POST', body: '{}' });

export const watchChallenge = (input: { token: string; initData: string; onSnapshot: (snapshot: ChallengeSnapshot) => void; onConnection: (connected: boolean) => void }): (() => void) => {
  const socket: Socket = io({ auth: { initData: input.initData, protocolVersion: SESSION_PROTOCOL_VERSION } });
  const webApp = window.Telegram?.WebApp;
  let disconnected = false;
  let disposed = false;
  let restoreInFlight: Promise<void> | null = null;
  let watchRetryTimer: number | null = null;
  let lastRestoreAt = 0;

  const clearWatchRetry = (): void => {
    if (watchRetryTimer !== null) window.clearTimeout(watchRetryTimer);
    watchRetryTimer = null;
  };

  const restoreAuthoritativeSnapshot = (mechanism: 'visibility' | 'telegram_activated' | 'window_focus' | 'pageshow' | 'online' | 'share_closed' | 'socket_changed'): void => {
    if (disposed) return;
    const now = Date.now();
    if (restoreInFlight || now - lastRestoreAt < 250) return;
    lastRestoreAt = now;
    trackAnalytics('recovery_attempted', { scope: 'challenge', mechanism });
    restoreInFlight = loadChallenge(input.token, input.initData).then((snapshot) => {
      if (disposed) return;
      input.onSnapshot(snapshot);
      trackAnalytics('snapshot_recovery_used', { scope: 'challenge', mechanism });
      trackAnalytics('recovery_succeeded', { scope: 'challenge', mechanism });
    }).catch(() => {
      if (!disposed) trackAnalytics('recovery_failed', { scope: 'challenge', mechanism });
    }).finally(() => {
      restoreInFlight = null;
    });
  };

  const subscribe = (): void => {
    if (disposed || !socket.connected) return;
    clearWatchRetry();
    socket.emit('challenge:watch', { token: input.token }, (result: { ok: boolean; snapshot?: ChallengeSnapshot }) => {
      if (disposed) return;
      if (result.ok && result.snapshot) {
        input.onSnapshot(result.snapshot);
        input.onConnection(true);
        if (disconnected) trackAnalytics('recovery_succeeded', { scope: 'challenge', mechanism: 'socket_resume' });
        disconnected = false;
        return;
      }
      input.onConnection(false);
      trackAnalytics('recovery_failed', { scope: 'challenge', mechanism: 'socket_watch' });
      watchRetryTimer = window.setTimeout(subscribe, 1_000);
    });
  };

  socket.on('connect', () => {
    input.onConnection(false);
    if (disconnected) trackAnalytics('recovery_attempted', { scope: 'challenge', mechanism: 'socket_resume' });
    subscribe();
  });
  socket.on('disconnect', () => {
    if (!disposed) {
      clearWatchRetry();
      disconnected = true;
      trackAnalytics('socket_disconnected', { scope: 'challenge' });
      input.onConnection(false);
    }
  });
  socket.on('challenge:changed', () => restoreAuthoritativeSnapshot('socket_changed'));

  const onVisibility = (): void => {
    if (document.visibilityState === 'visible') restoreAuthoritativeSnapshot('visibility');
  };
  const onActivated = (): void => {
    restoreAuthoritativeSnapshot('telegram_activated');
    subscribe();
  };
  const onFocus = (): void => restoreAuthoritativeSnapshot('window_focus');
  const onPageShow = (): void => restoreAuthoritativeSnapshot('pageshow');
  const onOnline = (): void => {
    restoreAuthoritativeSnapshot('online');
    subscribe();
  };
  const onShareClosed = (): void => {
    restoreAuthoritativeSnapshot('share_closed');
    subscribe();
  };

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('focus', onFocus);
  window.addEventListener('pageshow', onPageShow);
  window.addEventListener('online', onOnline);
  webApp?.onEvent('activated', onActivated);
  webApp?.onEvent('shareMessageSent', onShareClosed);
  webApp?.onEvent('shareMessageFailed', onShareClosed);

  return () => {
    disposed = true;
    clearWatchRetry();
    socket.disconnect();
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('focus', onFocus);
    window.removeEventListener('pageshow', onPageShow);
    window.removeEventListener('online', onOnline);
    webApp?.offEvent('activated', onActivated);
    webApp?.offEvent('shareMessageSent', onShareClosed);
    webApp?.offEvent('shareMessageFailed', onShareClosed);
  };
};
